import { labelForView, type AtlasEvidence } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { EASE, INK, TONE, clockText, mark, secondsSince, useGo } from './common';
import { BuildTagged } from '../../ui/buildLabels';

const GRID = 'display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:0 30px;min-width:0;';

/**
 * The evidence block under an answer or the opening brief (design: m.hasBlock, T13195).
 * Every row opens the page it came from.
 */
export function EvidenceBlock({ ev, now }: { ev: AtlasEvidence; now: number }) {
  const go = useGo();
  return (
    <div data-testid="atlas-evidence" style={sx('min-width:0;margin-top:11px;border-top:1px solid rgba(34,39,46,.16);padding-top:9px;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:1px;min-width:0;padding-bottom:2px;')}>
        <span style={sx('flex:0 0 auto;font-size:16px;font-weight:600;letter-spacing:-.01em;color:#22272E;')}>{ev.title}</span>
        <span style={sx('flex:1 1 0;min-width:0;font-size:14px;color:#54606C;text-align:right;')}>{ev.provenance}</span>
      </div>
      <div style={sx(GRID)}>
        {ev.items.map((r, k) => {
          const tone = TONE[r.figureTone] ?? INK;
          const fig = r.clockFrom ? clockText(secondsSince(r.clockFrom, now)) : r.figure ?? '–';
          const bar = r.bar;
          return (
            <a
              key={k}
              href="#"
              onClick={go(r.view)}
              title={r.view ? 'Open ' + labelForView(r.view) : undefined}
              style={sx('display:block;padding:10px 0;border-top:1px solid rgba(34,39,46,.14);text-decoration:none;min-width:0;color:' + INK + ';transition:color .15s ' + EASE + ';')}
            >
              <span style={sx('display:flex;align-items:center;gap:8px;min-width:0;')}>
                <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;font-weight:700;line-height:1.35;color:inherit;overflow-wrap:break-word;')}>{r.name}</span>
                <BuildTagged tag={r.badge}><span style={sx(mark(r.badgeTone))}>{r.badge}</span></BuildTagged>
                <span style={sx('flex:0 1 auto;min-width:0;max-width:40%;font-size:14px;color:#54606C;text-align:right;font-variant-numeric:tabular-nums;overflow-wrap:break-word;')}>{r.meta}</span>
              </span>
              <span style={sx('display:flex;align-items:center;gap:7px;margin-top:3px;min-width:0;')}>
                <span style={sx('flex:0 0 46px;font-size:14px;color:#54606C;')}>{bar?.label ?? ''}</span>
                <span style={sx('flex:0 1 210px;min-width:0;display:block;')}>
                  <span style={sx(bar ? 'display:block;height:6px;border-radius:2px;background:' + tone + ';width:' + bar.current + '%;' : 'display:none;')} />
                  <span style={sx(bar ? 'display:block;height:3px;border-radius:2px;margin-top:2px;background:rgba(34,39,46,.24);width:' + bar.baseline + '%;' : 'display:none;')} />
                </span>
                <span style={sx('flex:0 0 auto;font-size:14px;font-weight:600;font-variant-numeric:tabular-nums;color:' + tone + ';')}>{fig}</span>
                <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{bar?.was ?? ''}</span>
              </span>
              <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.4;margin-top:2px;text-wrap:pretty;')}>{r.note}</span>
            </a>
          );
        })}
      </div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.45;margin-top:6px;text-wrap:pretty;')}>{ev.foot}</div>
    </div>
  );
}
